import React, { useMemo, useState } from "react";
import { injectIntl, intlShape } from "react-intl";

import {
    DialogBox,
    DialogBoxFooterType,
    NoLink,
    Section,
    TextInput,
    ZeroToInfiniteGrid,
} from "~/core";
import { messages } from "../../i18n-messages";
import { createAddLinkLabelText } from "~/i18n-messages";

import "./system-custom-menu.css";
import { ISystemCustomMenu } from "@ai360/core/dist/4x/es/api/user";

interface IProps {
    intl: intlShape;
    list: ISystemCustomMenu[];
    updateList: (values: ISystemCustomMenu[]) => void;
}

function SystemCustomMenu_(props: IProps) {
    const { formatMessage } = props.intl;
    const { list } = props;

    const [showDialog, setShowDialog] = useState(false);
    const [editRow, setEditRow] = useState<number | null>(null);

    const getOrdinalIndicator = (editRow: number | null, list: any[]): string => {
        const num = editRow !== null ? editRow + 1 : list?.length + 1;
        const lastDigit = num % 10;
        const lastTwoDigits = num % 100;

        if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
            return `${num}th`;
        }

        switch (lastDigit) {
            case 1:
                return `${num}st`;
            case 2:
                return `${num}nd`;
            case 3:
                return `${num}rd`;
            default:
                return `${num}th`;
        }
    };

    const [name, setName] = useState<string>(null);
    const [url, setUrl] = useState<string>(null);

    const closeDialog = () => {
        setShowDialog(false);
        setEditRow(null);
        setName(null);
        setUrl(null);
    };

    const deleteHandler = (_, index) => {
        const newList = list.filter((x, i) => i !== index);
        props.updateList(newList);
    };

    const displayRecords = useMemo<ISystemCustomMenu[]>(
        () =>
            list?.map((r) => ({
                ...r,
            })) ?? [],
        [list]
    );

    const invalidSave = useMemo(() => [name, url].findIndex((x) => !x) !== -1, [name, url]);

    return (
        <Section
            headerText={formatMessage(messages.systemCustomMenuLabel)}
            className="grid-section system-custom-menu"
        >
            <div className="form-section-child-stretch mini-grid">
                {displayRecords.length > 0 && (
                    <ZeroToInfiniteGrid
                        className={"target-level-grid"}
                        records={displayRecords}
                        onEdit={(record: ISystemCustomMenu, index) => {
                            setEditRow(index);
                            setName(record.name);
                            setUrl(record.url);
                            setShowDialog(true);
                        }}
                        onDelete={deleteHandler}
                        columns={{
                            name: {
                                title: formatMessage(messages.systemCustomMenuNameLabel),
                            },
                            url: {
                                title: formatMessage(messages.systemCustomMenuUrlLabel),
                                className: "row-size",
                            },
                        }}
                    />
                )}
                <div className="add-link-container">
                    <NoLink
                        className="add-link"
                        label={createAddLinkLabelText(
                            formatMessage,
                            messages.systemCustomMenuAddLabel
                        )}
                        onClick={() => {
                            setShowDialog(true);
                        }}
                    />
                </div>
            </div>
            {showDialog && (
                <DialogBox
                    isOpen={true}
                    unrestricted={true}
                    className="add-edit-dialog"
                    actionDisabled={invalidSave}
                    footerType={DialogBoxFooterType.ACTION_CANCEL}
                    action={formatMessage(messages.save)}
                    title={`${formatMessage(
                        editRow !== null ? messages.edit : messages.add
                    )} ${formatMessage(
                        messages.systemCustomMenuDialogLabel
                    )} - ${getOrdinalIndicator(editRow, list)}`}
                    onAction={() => {
                        const row: ISystemCustomMenu = {
                            systemCustomMenuGuid:
                                editRow !== null ? list[editRow].systemCustomMenuGuid : null,
                            name,
                            url,
                            sortOrder: editRow != null ? list[editRow].sortOrder : list?.length + 1,
                        };

                        const newList = [...list];

                        if (editRow !== null) {
                            newList[editRow] = row;
                        } else {
                            newList.push(row);
                        }

                        props.updateList(newList);
                        closeDialog();
                    }}
                    onClose={closeDialog}
                >
                    <TextInput
                        placeholderText={formatMessage(messages.systemCustomMenuNameLabel)}
                        required
                        value={name}
                        onChange={(value) => setName(value)}
                    />
                    <TextInput
                        placeholderText={formatMessage(messages.systemCustomMenuUrlLabel)}
                        required
                        value={url}
                        onChange={(value) => setUrl(value)}
                    />
                </DialogBox>
            )}
        </Section>
    );
}

export default injectIntl(SystemCustomMenu_);
